import { FC, useState, useMemo } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useQuery, useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { observer } from 'mobx-react-lite';
import { Search } from 'components/Search';
import { ConfirmActionDialog, ConfirmActionDialogState } from 'components/Dialogs';
import { GridColumns, DataGrid, GridCellParams } from '@material-ui/data-grid';
import IconButton from '@material-ui/core/IconButton';
import PersonIcon from '@material-ui/icons/Person';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { Grid, Button } from '@material-ui/core';
import { useStyles } from './styles';
import { CircularProgress, Paper } from '@material-ui/core';
import clsx from 'clsx';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import { useHistory } from 'react-router-dom';
import PreviewIcon from '@material-ui/icons/RemoveRedEyeSharp';
import { orange } from '@material-ui/core/colors';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { getRowsPerPageOptions } from 'utils/pagination';
import { Switcher } from 'components/Switcher';
import EmailIcon from './../../../assets/icons/EndUser/Email.svg';
import { Tooltip } from '@material-ui/core';
import { toFormatDateTime } from 'utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';

export const AdminsTable: FC = observer(() => {
  const {
    ldapStore: { deleteUser },
    adminsTableStore: { getAdmins, lockUser, adminsView, setClickedUser, setFilter },
    endUserSettingsFormStore: { portalSettingsget },
    notificationStore: { openNotification },
  } = useStore();
  const classes = useStyles();
  const history = useHistory();

  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState<ConfirmActionDialogState>({ open: false });
  const [openConfirmLockDialog, setOpenConfirmLockDialog] = useState<ConfirmActionDialogState>({ open: false });
  const [notifyStatus, setNotifyStatus] = useState<boolean>(true);
  const [appStatus, setAppStatus] = useState<boolean>(false);
  const getUsersReq = useQuery('getUsersReq', () => getAdmins(), {
    onError: () => openNotification('error', `Can't get admin users`),
  });

  const profile = JSON.parse(localStorage.getItem('Profile') as string);
  const permission = profile?.permission;
  const portalSettingsgetReq = useQuery('portalSettingsgetReq', () => portalSettingsget(), {
    onError: () => openNotification('error', `Can't get data`),
  });
  const endUserConfigSettings = JSON.parse(localStorage.getItem('endUserConfigSettings') as string);
  const userId = endUserConfigSettings?.userId;
  const deleteUserReq = useMutation((dn: string) => deleteUser(dn, appStatus, permission), {
    mutationKey: 'deleteUserReq',
    onSuccess: () => {
      openNotification('success', `Deleted succcessfully`);
      getUsersReq.refetch();
    },
    onError: () => openNotification('error', `Can't delete user`),
  });

  const lockUserReq = useMutation((dn: string) => lockUser(dn, notifyStatus, permission), {
    mutationKey: 'lockUserReq',
    onSuccess: () => {
      openNotification('success', `Successfully Updated`);
      getUsersReq.refetch();
    },
    onError: () => openNotification('error', `Can't Update`),
  });

  useAppPreloader(
    getUsersReq.isFetching || deleteUserReq.isLoading || lockUserReq.isLoading || portalSettingsgetReq.isLoading
  );
  const rows = useMemo(() => {
    return !getUsersReq.isFetching ? adminsView : [];
  }, [getUsersReq.isFetching, adminsView]);
  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);
  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => <PersonIcon style={{ color: 'grey' }} />,
    },
    {
      field: 'createtimestamp',
      headerName: 'CREATED',
      flex: 0.7,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={toFormatDateTime(params.row.createtimestamp.toString())} placement='bottom'>
          <div style={{ display: 'flex', gap: '10px' }}>{toFormatDateTime(params.row.createtimestamp.toString())}</div>
        </Tooltip>
      ),
    },
    {
      field: 'modifytimestamp',
      headerName: 'UPDATED',
      flex: 0.7,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={toFormatDateTime(params.row.modifytimestamp.toString())} placement='bottom'>
          <div style={{ display: 'flex', gap: '10px' }}>{toFormatDateTime(params.row.modifytimestamp.toString())}</div>
        </Tooltip>
      ),
    },
    {
      field: 'name',
      headerName: 'FULL NAME',
      flex: 0.5,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={(params.row.givenname || '') + ' ' + (params.row.sn || '')}>
          <div>
            <span>
              {params.row.givenname} {params.row.sn}
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'carlicense',
      headerName: 'APPLICATION ROLE',
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        const roleMap: Record<string, string> = {
          billing_admin: 'Billing Administrator',
          system_admin: 'System Administrator',
        };

        const value = String(params.value ?? '');
        const displayValue = roleMap[value] ?? value;

        return (
          <Tooltip title={displayValue}>
            <div>
              <span>{displayValue}</span>
            </div>
          </Tooltip>
        );
      },

      // renderCell: (params: GridCellParams) => {
      //   const roleMap: { [key: string]: string } = {
      //     'billing_admin': 'Billing Administrator',
      //     'system_admin': 'System Administrator'
      //   };
      //   const displayValue = roleMap[params.value as string] || params.value || '';
      //   return (
      //     <Tooltip title={displayValue}>
      //       <div>
      //         <span>{displayValue}</span>
      //       </div>
      //     </Tooltip>
      //   );
      // },
    },
    {
      field: 'uid',
      headerName: endUserConfigSettings?.userId === null || endUserConfigSettings?.userId === '' ? 'User Id' : userId,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');

        return (
          <Tooltip title={value}>
            <div>
              <span onClick={() => history.replace(handleCompanyUrl('/admin/view'))} style={{ color: 'blue' }}>
                {value}
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'mail',
      headerName: 'EMAIL',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');

        return (
          <Tooltip title={value} placement='bottom'>
            <div style={{ display: 'flex', gap: '10px' }}>
              <img src={EmailIcon} alt='' />
              <span>{value}</span>
            </div>
          </Tooltip>
        );
      },
    },

    // {
    //   field: 'uid',
    //   headerName: endUserConfigSettings?.userId === null || endUserConfigSettings?.userId === '' ? 'User Id' : userId,
    //   flex: 0.5,
    //   renderCell: (params: GridCellParams) => (
    //     <Tooltip title={params.value || ''}>
    //       <div>
    //         <span onClick={() => history.replace(handleCompanyUrl('/admin/view'))} style={{ color: 'blue' }}>
    //           {params.value}
    //         </span>
    //       </div>
    //     </Tooltip>
    //   ),
    // },
    // {
    //   field: 'mail',
    //   headerName: 'EMAIL',
    //   flex: 1,
    //   renderCell: (params: GridCellParams) => (
    //     <Tooltip title={params.value || ''} placement='bottom'>
    //       <div style={{ display: 'flex', gap: '10px' }}>
    //         <img src={EmailIcon} alt='' />
    //         <span>{params.value}</span>
    //       </div>
    //     </Tooltip>
    //   ),
    // },
    {
      field: 'googleId',
      headerName: 'SYNC',
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        const { googleId, zoomId, microsoftId } = params.row;
        const thirdConnected = googleId && zoomId && microsoftId;
        const bothConnected = (googleId && zoomId) || (googleId && microsoftId) || (zoomId && microsoftId);
        const onlyOneConnected = googleId || zoomId || microsoftId;
        return (
          <Tooltip
            title={
              thirdConnected
                ? 'Google Workspace, Zoom Workspace, Microsoft Workspcae'
                : bothConnected
                ? params.row.googleId && params.row.zoomId
                  ? 'Google Workspace, Zoom Workspace'
                  : params.row.googleId && params.row.microsoftId
                  ? 'Google Workspace, Microsoft Workspace'
                  : params.row.zoomId && params.row.microsoftId
                  ? 'Zoom Workspace, Microsoft Workspace'
                  : 'Not Connected'
                : onlyOneConnected
                ? params.row.googleId
                  ? 'Google Workspace'
                  : params.row.zoomId
                  ? 'Zoom Workspace'
                  : 'Microsoft Workspace'
                : 'Not Connected'
            }
            placement='bottom'
          >
            <div style={{ display: 'flex', gap: '10px', position: 'relative' }}>
              {onlyOneConnected ? (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  {thirdConnected ? (
                    <>
                      <CircularProgress
                        variant='determinate'
                        value={100}
                        size={40}
                        style={{ color: 'green', position: 'absolute', zIndex: 1 }}
                      />
                      <CheckCircleIcon
                        style={{
                          color: 'green',
                          fontSize: '24px',
                          zIndex: 2,
                          margin: '8px',
                        }}
                      />
                    </>
                  ) : bothConnected ? (
                    <>
                      <CircularProgress
                        variant='determinate'
                        value={100}
                        size={40}
                        style={{ color: 'green', position: 'absolute', zIndex: 1 }}
                      />
                      <CircularProgress
                        variant='determinate'
                        value={33.3}
                        size={40}
                        style={{ color: 'grey', position: 'absolute', zIndex: 2 }}
                      />
                      <CheckCircleIcon
                        style={{
                          color: 'green',
                          fontSize: '24px',
                          zIndex: 3,
                          margin: '8px',
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <CircularProgress
                        variant='determinate'
                        value={100}
                        size={40}
                        style={{ color: 'green', position: 'absolute', zIndex: 1 }}
                      />
                      <CircularProgress
                        variant='determinate'
                        value={66.6}
                        size={40}
                        style={{ color: 'grey', position: 'absolute', zIndex: 2 }}
                      />
                      <CheckCircleIcon
                        style={{
                          color: 'green',
                          fontSize: '24px',
                          zIndex: 3,
                          margin: '8px',
                        }}
                      />
                    </>
                  )}
                </div>
              ) : (
                <>
                  <CircularProgress
                    variant='determinate'
                    value={100}
                    size={40}
                    style={{ color: 'grey', position: 'absolute', zIndex: 1 }}
                  />
                  <CloseIcon
                    style={{
                      color: 'red',
                      fontSize: '24px',
                      zIndex: 2,
                      margin: '8px',
                    }}
                  />
                </>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 160,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => (
        <>
          <Switcher
            checked={!(!!params.row.locked as boolean)}
            onClick={() =>
              setOpenConfirmLockDialog({
                open: true,
                action: () => lockUserReq.mutate(params.row.dn),
                lockStatus: params.row.locked,
              })
            }
          />
          <Tooltip title='View Admin'>
            <IconButton size='small' onClick={() => history.replace(handleCompanyUrl('/admin/view'))}>
              <PreviewIcon style={{ color: orange[500] }} />
            </IconButton>
          </Tooltip>
          <Tooltip title='Edit Admin' placement='bottom'>
            <IconButton size='small' onClick={() => history.push(handleCompanyUrl('/admin/edit'))}>
              <EditIcon style={{ fill: '#FF9800' }} />
            </IconButton>
          </Tooltip>
          <IconButton
            size='small'
            onClick={() => {
              setOpenConfirmDeleteDialog({
                open: true,
                action: () => deleteUserReq.mutate(params.row.dn),
                appStatus: false,
              });
            }}
          >
            <Tooltip title='Deleted Admin'>
              <DeleteIcon style={{ fill: '#F44336' }} />
            </Tooltip>
          </IconButton>
        </>
      ),
    },
  ];
  const formattedRows = rows.map((row: { modifytimestamp: string | null; createtimestamp: string | null }) => ({
    ...row,
  }));
  const SaveCkickedUser = (dn: any) => {
    const dataOfUser = { ...getUsersReq.data.find((user: { dn: any }) => user.dn === dn) };
    localStorage.setItem('ClickedUser', JSON.stringify(dataOfUser));
    setClickedUser(dn);
  };
  return (
    <>
      <Grid container justify='space-between' alignItems='center' className={classes.box}>
        <HomeWorkIcon style={{ color: 'grey' }} />
        <Search handelSearch={setFilter} />

        <Grid>
          <Button
            className={clsx(classes.headerBtn, classes.addNewButton)}
            size='small'
            variant='contained'
            color='secondary'
            onClick={() => history.replace(handleCompanyUrl('/admin/add'))}
          >
            <AddCircleOutlineIcon />
          </Button>
        </Grid>
      </Grid>

      <Paper className={classes.dataGrid} elevation={0}>
        <DataGrid
          className={classes.noBorder}
          autoHeight
          rows={formattedRows}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          onPageChange={(params) => setPageSize(params.pageSize)}
          onCellClick={(event: GridCellParams) => SaveCkickedUser(event.row.dn)}
        />
      </Paper>
      <ConfirmActionDialog
        label='Are you sure you want to delete this user?'
        appnotify={appStatus}
        appnotifyStatus={setAppStatus}
        carlicense='admin'
        buttonLabel='Delete'
        gitStatus=''
        openModal={openConfirmDeleteDialog.open}
        action={openConfirmDeleteDialog.action}
        handleClose={() => {
          setOpenConfirmDeleteDialog({
            open: false,
            action: undefined,
          });
          setAppStatus(true);
        }}
      />
      <ConfirmActionDialog
        label={openConfirmLockDialog.lockStatus === true ? 'Unlock User' : 'Lock User'}
        notify={notifyStatus}
        gitStatus=''
        notifyStatus={setNotifyStatus}
        buttonLabel='Confirm'
        openModal={openConfirmLockDialog.open}
        action={openConfirmLockDialog.action}
        handleClose={() => {
          setOpenConfirmLockDialog({
            open: false,
            action: undefined,
          });
          setNotifyStatus(true);
        }}
      />
    </>
  );
});
